import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DataObjectIcon from '@mui/icons-material/DataObject';
import BugReportIcon from '@mui/icons-material/BugReport';
import api from '../../../services/api';

const TestingSimulation = () => {
  const [, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  
  const [testingMode, setTestingMode] = useState({
    enabled: false,
    simulationEnabled: false,
    testDataCount: 10,
    testDataType: 'projects',
    simulationSpeed: 'normal'
  });
  
  const [testDataTypes] = useState([
    { value: 'projects', label: 'Projects' },
    { value: 'users', label: 'Users' },
    { value: 'tasks', label: 'Tasks' },
    { value: 'progress', label: 'Progress Reports' },
    { value: 'issues', label: 'Issues' },
    { value: 'all', label: 'All Data Types' }
  ]);
  
  const [simulationSpeeds] = useState([
    { value: 'slow', label: 'Lambat (1x per jam)' },
    { value: 'normal', label: 'Normal (1x per 15 menit)' },
    { value: 'fast', label: 'Cepat (1x per menit)' }
  ]);
  
  const [testScenarios, setTestScenarios] = useState([
    { id: 1, name: 'Proyek Terlambat', description: 'Simulasi proyek dengan progress terlambat', status: 'ready' },
    { id: 2, name: 'Masalah Kritis', description: 'Simulasi masalah kritis yang memerlukan perhatian segera', status: 'ready' },
    { id: 3, name: 'Overbudget', description: 'Simulasi proyek dengan pengeluaran melebihi anggaran', status: 'ready' }
  ]);
  
  const [testLogs, setTestLogs] = useState([]);
  
  // Menggunakan useCallback untuk memastikan fungsi fetchTestingSettings stabil antar render
  const fetchTestingSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/testing/settings').catch(() => {
        // If API endpoint doesn't exist yet, use default settings
        return { data: { settings: testingMode } };
      });
      
      setTestingMode(response.data.settings);
    } catch (error) {
      console.error('Error fetching testing settings:', error);
      setAlert({
        open: true,
        message: 'Gagal memuat pengaturan testing',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [testingMode, setAlert]);
  
  // Menggunakan useCallback untuk fungsi fetchTestLogs
  const fetchTestLogs = useCallback(async () => {
    try {
      const response = await api.get('/admin/testing/logs').catch(() => {
        // If API endpoint doesn't exist yet, use default logs
        return { data: { logs: [] } };
      });
      
      setTestLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching test logs:', error);
    }
  }, []);
  
  useEffect(() => {
    fetchTestingSettings();
    fetchTestLogs();
  }, [fetchTestingSettings, fetchTestLogs]);
  
  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.post('/admin/testing/settings', { settings: testingMode }).catch(() => {
        // If API endpoint doesn't exist yet, just simulate success
      });
      
      setAlert({
        open: true,
        message: 'Pengaturan testing berhasil disimpan',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving testing settings:', error);
      setAlert({
        open: true,
        message: 'Gagal menyimpan pengaturan testing',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };
  
  const handleGenerateTestData = async () => {
    setConfirmDialog({
      open: true,
      title: 'Generate Test Data',
      message: `Apakah Anda yakin ingin membuat ${testingMode.testDataCount} data ${testDataTypes.find(t => t.value === testingMode.testDataType)?.label.toLowerCase() || 'test'} baru?`,
      onConfirm: async () => {
        setSaving(true);
        try {
          await api.post('/admin/testing/generate', {
            count: testingMode.testDataCount,
            type: testingMode.testDataType
          }).catch(() => {
            // If API endpoint doesn't exist yet, just simulate success
          });
          
          setAlert({
            open: true,
            message: `${testingMode.testDataCount} data test berhasil dibuat`,
            severity: 'success'
          });
          
          // Refresh logs
          fetchTestLogs();
        } catch (error) {
          console.error('Error generating test data:', error);
          setAlert({
            open: true,
            message: 'Gagal membuat data test',
            severity: 'error'
          });
        } finally {
          setSaving(false);
          setConfirmDialog({ ...confirmDialog, open: false });
        }
      }
    });
  };
  
  const handleRunScenario = async (scenarioId) => {
    const scenario = testScenarios.find(s => s.id === scenarioId);
    
    setConfirmDialog({
      open: true,
      title: 'Run Test Scenario',
      message: `Apakah Anda yakin ingin menjalankan skenario "${scenario?.name}"?`,
      onConfirm: async () => {
        // Update scenario status
        setTestScenarios(testScenarios.map(s => 
          s.id === scenarioId ? { ...s, status: 'running' } : s
        ));
        
        try {
          await api.post('/admin/testing/scenario', { scenarioId }).catch(() => {
            // If API endpoint doesn't exist yet, just simulate success
          });
          
          // Simulate completion after 3 seconds
          setTimeout(() => {
            setTestScenarios(testScenarios.map(s => 
              s.id === scenarioId ? { ...s, status: 'completed' } : s
            ));
            
            setAlert({
              open: true,
              message: `Skenario "${scenario?.name}" berhasil dijalankan`,
              severity: 'success'
            });
            
            // Refresh logs
            fetchTestLogs();
            
            // Reset status to ready after 5 more seconds
            setTimeout(() => {
              setTestScenarios(testScenarios.map(s => 
                s.id === scenarioId ? { ...s, status: 'ready' } : s
              ));
            }, 5000);
          }, 3000);
        } catch (error) {
          console.error('Error running test scenario:', error);
          setAlert({
            open: true,
            message: `Gagal menjalankan skenario "${scenario?.name}"`,
            severity: 'error'
          });
          
          // Reset status to ready
          setTestScenarios(testScenarios.map(s => 
            s.id === scenarioId ? { ...s, status: 'ready' } : s
          ));
        } finally {
          setConfirmDialog({ ...confirmDialog, open: false });
        }
      }
    });
  };
  
  const handleClearTestData = async () => {
    setConfirmDialog({
      open: true,
      title: 'Clear Test Data',
      message: 'Apakah Anda yakin ingin menghapus semua data test? Tindakan ini tidak dapat dibatalkan.',
      onConfirm: async () => {
        setSaving(true);
        try {
          await api.delete('/admin/testing/data').catch(() => {
            // If API endpoint doesn't exist yet, just simulate success
          });
          
          setAlert({
            open: true,
            message: 'Semua data test berhasil dihapus',
            severity: 'success'
          });
          
          // Refresh logs
          fetchTestLogs();
        } catch (error) {
          console.error('Error clearing test data:', error);
          setAlert({
            open: true,
            message: 'Gagal menghapus data test',
            severity: 'error'
          });
        } finally {
          setSaving(false);
          setConfirmDialog({ ...confirmDialog, open: false });
        }
      }
    });
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTestingMode(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setTestingMode(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  
  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };
  
  const handleCloseConfirmDialog = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };
  
  const getScenarioStatusColor = (status) => {
    switch (status) {
      case 'ready':
        return 'primary';
      case 'running':
        return 'warning';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };
  
  const getScenarioStatusLabel = (status) => {
    switch (status) {
      case 'ready':
        return 'Siap';
      case 'running':
        return 'Berjalan';
      case 'completed':
        return 'Selesai';
      case 'failed':
        return 'Gagal';
      default:
        return status;
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Simulasi & Testing</Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <BugReportIcon sx={{ mr: 2 }} />
          <Typography variant="h6">Mode Testing</Typography>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={testingMode.enabled}
                  onChange={handleSwitchChange}
                  name="enabled"
                  color="primary"
                />
              }
              label="Aktifkan Mode Testing"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Mode testing memungkinkan Anda untuk membuat data test dan menjalankan skenario simulasi.
              Aktifkan mode ini hanya di lingkungan pengembangan atau staging.
            </Typography>
          </Grid>
          
          {testingMode.enabled && (
            <>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Pengaturan Data Test</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Jumlah Data Test"
                  type="number"
                  name="testDataCount"
                  value={testingMode.testDataCount}
                  onChange={handleInputChange}
                  inputProps={{ min: 1, max: 100 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipe Data</InputLabel>
                  <Select
                    name="testDataType"
                    value={testingMode.testDataType}
                    label="Tipe Data"
                    onChange={handleInputChange}
                  >
                    {testDataTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 2 }}>Pengaturan Simulasi</Typography>
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={testingMode.simulationEnabled}
                      onChange={handleSwitchChange}
                      name="simulationEnabled"
                      color="primary"
                    />
                  }
                  label="Aktifkan Simulasi Otomatis"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Simulasi otomatis akan menjalankan skenario secara berkala berdasarkan kecepatan yang dipilih.
                </Typography>
              </Grid>
              
              {testingMode.simulationEnabled && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Kecepatan Simulasi</InputLabel>
                    <Select
                      name="simulationSpeed"
                      value={testingMode.simulationSpeed}
                      label="Kecepatan Simulasi"
                      onChange={handleInputChange}
                    >
                      {simulationSpeeds.map(speed => (
                        <MenuItem key={speed.value} value={speed.value}>
                          {speed.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSaveSettings}
                    disabled={saving}
                  >
                    {saving ? <CircularProgress size={24} /> : 'Simpan Pengaturan'}
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={handleGenerateTestData}
                    disabled={saving}
                    startIcon={<DataObjectIcon />}
                  >
                    Generate Data Test
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    color="error" 
                    onClick={handleClearTestData}
                    disabled={saving}
                    startIcon={<DeleteIcon />}
                  >
                    Hapus Semua Data Test
                  </Button>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
      
      {testingMode.enabled && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">Skenario Test</Typography>
            <Button 
              variant="outlined" 
              startIcon={<AddIcon />}
              onClick={() => {/* Implement add scenario */}}
            >
              Tambah Skenario
            </Button>
          </Box>
          
          <List>
            {testScenarios.map((scenario) => (
              <ListItem
                key={scenario.id}
                secondaryAction={
                  <IconButton 
                    edge="end" 
                    color="primary"
                    onClick={() => handleRunScenario(scenario.id)}
                    disabled={scenario.status === 'running'}
                  >
                    <PlayArrowIcon />
                  </IconButton>
                }
              >
                <ListItemIcon>
                  <BugReportIcon />
                </ListItemIcon>
                <ListItemText
                  primary={scenario.name}
                  secondary={scenario.description}
                />
                <Chip 
                  label={getScenarioStatusLabel(scenario.status)} 
                  color={getScenarioStatusColor(scenario.status)} 
                  size="small" 
                  sx={{ mr: 2 }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
      
      {testingMode.enabled && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">Log Aktivitas Test</Typography>
            <Button 
              variant="text" 
              startIcon={<RefreshIcon />}
              onClick={fetchTestLogs}
            >
              Refresh
            </Button>
          </Box>
          
          {testLogs.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign: 'center', py: 3 }}>
              Belum ada aktivitas test
            </Typography>
          ) : (
            <List>
              {testLogs.map((log, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={log.action}
                    secondary={`${new Date(log.timestamp).toLocaleString()} - ${log.details}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}
      
      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Batal</Button>
          <Button 
            onClick={() => confirmDialog.onConfirm && confirmDialog.onConfirm()} 
            color="primary" 
            autoFocus
          >
            Konfirmasi
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Alert */}
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity={alert.severity} 
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TestingSimulation;
